//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import React from "react";
import moment from "moment";
import { Button, ButtonGroup, Checkbox, CheckboxProps, Form } from "semantic-ui-react";
import { NotificationSeverity } from "../../api/lib";
import { dateForDateTimeInputValue } from "../../util/dateAndTimeUtils/DateTime";
import { DateTimePicker } from "./DateTimePicker";

export type NotificationData = {
  notificationText: string;
  severity: NotificationSeverity;
  notificationId: string;
  expiresAt?: Date;
};

// Notifications expire after a week unless the operator explicitly disables expiration.
export const DEFAULT_EXPIRATION_DAYS = 7;

export const defaultExpiresAt = (): Date => moment().add(DEFAULT_EXPIRATION_DAYS, "days").toDate();

export const emptyNotificationData = (): NotificationData => ({
  notificationId: "",
  notificationText: "",
  severity: NotificationSeverity.NOTIFICATION_SEVERITY_INFO,
  expiresAt: defaultExpiresAt(),
});

// NotificationFields renders the form fields shared by every place that creates a notification
// (single deployment/organization notification modal and the deployment updater job).
export const NotificationFields = ({
  value,
  onChange,
  disabled,
  idReadOnly,
  idFormat,
}: {
  value: NotificationData;
  onChange: (value: NotificationData) => void;
  disabled?: boolean;
  // idReadOnly keeps the notification ID visible but not editable (e.g. when editing an existing rule).
  idReadOnly?: boolean;
  // idFormat restricts the notification ID: the hint (with an example) is shown under the field
  // and the field is marked invalid while a typed ID does not match the pattern.
  idFormat?: { pattern: RegExp; hint: string; example: string };
}) => {
  const idInvalid = !!idFormat && !!value.notificationId && !idFormat.pattern.test(value.notificationId);
  const severityButton = (severity: NotificationSeverity, content: string) => (
    <Button type="button" content={content} disabled={disabled} primary={value.severity === severity} onClick={() => onChange({ ...value, severity })} />
  );
  return (
    <>
      <Form.Field>
        <Form.Input
          name="notificationId"
          value={value.notificationId}
          label="Notification ID"
          placeholder={idFormat ? `e.g. ${idFormat.example}` : "Notification ID"}
          required
          disabled={disabled}
          readOnly={idReadOnly}
          error={idInvalid}
          onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => {
            onChange({ ...value, notificationId: target.value });
          }}
        />
        {idFormat && !idReadOnly && (
          <small style={idInvalid ? { color: "#9f3a38" } : undefined}>
            {idFormat.hint}, e.g. <code>{idFormat.example}</code>
          </small>
        )}
      </Form.Field>
      <Form.Field>
        <Form.TextArea
          name="notification"
          value={value.notificationText}
          label="Notification"
          placeholder="Notification"
          required
          disabled={disabled}
          onChange={({ target }: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange({ ...value, notificationText: target.value });
          }}
        />
      </Form.Field>
      <Form.Field>
        <label>Expires at</label>
        <p>
          <small>
            Defaults to {DEFAULT_EXPIRATION_DAYS} days from now. After this time the notification is no longer shown and is removed by the inspector.
          </small>
        </p>
        <Checkbox
          toggle
          label="Never expires"
          disabled={disabled}
          checked={!value.expiresAt}
          onChange={(event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
            onChange({ ...value, expiresAt: data.checked ? undefined : defaultExpiresAt() });
          }}
        />
        {value.expiresAt && (
          // Rendered conditionally so the picker re-mounts with the default value after "Never expires" is switched off.
          <DateTimePicker
            dateTime={dateForDateTimeInputValue(value.expiresAt)}
            onClear={() => {
              onChange({ ...value, expiresAt: undefined });
            }}
            onChange={(newDateTime) => {
              onChange({ ...value, expiresAt: newDateTime ? new Date(newDateTime) : undefined });
            }}
          />
        )}
      </Form.Field>
      <Form.Field>
        <label>Severity</label>
        <ButtonGroup>
          {severityButton(NotificationSeverity.NOTIFICATION_SEVERITY_INFO, "Info")}
          {severityButton(NotificationSeverity.NOTIFICATION_SEVERITY_WARNING, "Warning")}
          {severityButton(NotificationSeverity.NOTIFICATION_SEVERITY_CRITICAL, "Critical")}
        </ButtonGroup>
      </Form.Field>
    </>
  );
};
